import { FC, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Controller, useFormContext } from "react-hook-form";
// Redux
import { useAppDispatch, useAppSelector } from "hooks/useStore";
// Types
import MediaFormats from "types/MediaFormats";
import QuizTypes from "types/QuizTypes";
// Models
import IQuestion from "models/Question";
// Async
import { createQuiz, updateQuiz } from "store/quiz/quizzesAsync";
// Selectors
import { getNamespaceItemsForSelect } from "store/namespace/namespaceSelectors";
import { selectQuiz, selectLoading } from "store/quiz/quizzesSelectors";
// MUI
import { ButtonGroup } from "@material-ui/core";
// Components
import { Input, Select, Toggle } from "components/Controls";
import DatePicker from "components/DatePicker";
import { Button } from "components/Buttons";
import StyledMuiButton from "components/StyledMuiButton";
import ImageUpload from "components/ImageUpload";
import { Dialog, DialogActions, DialogContent, DialogTitle } from "layouts/Dialog";
// Utilities
import { isRequired, noWhitespace } from "utilities/validation";
import { formValuesToQuiz } from "utilities/quizFormMapping";

type Props = {
  open: boolean;
  onClose: () => void;
  onCloseAgentDialog: () => void;
}

const UNNECESSARY_KEYS = ['account', 'updatedBy', 'createdAt', 'updatedAt', '__v', '_id'];

const QuizAgentFinalizeDialog:FC<Props> = ({ open, onClose, onCloseAgentDialog }) => {
  const { t } = useTranslation();

  const dispatch = useAppDispatch();

  const quiz = useAppSelector(selectQuiz);
  const loading = useAppSelector(selectLoading);
  const namespaceOptions = useAppSelector(getNamespaceItemsForSelect);

  const { control, handleSubmit, formState: { errors }, watch, getValues } = useFormContext<any>();

  const logoFormatWatcher = watch('logoFormat');
  const logoImageWatcher = watch('logoImage');
  const typeWatcher = watch('type');

  // Apply the small set of normalizations the agent flow needs on top of the
  // shared formValuesToQuiz mapping: ensure question descriptions exist,
  // bucketScores arrays are present, and numeric string fields (matching
  // QuizFormPage's submit logic) are converted to numbers.
  const normalizeBucketScores = (bucketScores: any[]) =>
    (bucketScores || []).map((bucketScore: any) => ({
      ...bucketScore,
      score: Number(bucketScore.score),
    }));

  const normalizeQuestions = (questions: any[]) =>
    (questions || []).map((question: IQuestion | any) => {
      const { score, timeout, ...rest } = question;
      const config = question.config || {};
      return {
        ...rest,
        // Convert numeric string fields to numbers, matching QuizFormPage's
        // submit logic. score is omitted when empty (no score set); timeout
        // defaults to 0 so the API always receives a valid value.
        ...(score ? { score: Number(score) } : {}),
        timeout: timeout ? Number(timeout) : 0,
        description: question.description || '',
        answers: (question.answers || []).map((answer: any) => ({
          ...answer,
          bucketScores: normalizeBucketScores(answer.bucketScores)
        })),
        config: {
          ...config,
          choices: config.choices?.map((choice: any) => ({
            ...choice,
            bucketScores: normalizeBucketScores(choice.bucketScores)
          })) || [],
          ...(config.referenceAnswer ? {
            referenceAnswer: {
              ...config.referenceAnswer,
              score: Number(config.referenceAnswer.score)
            }
          } : {}),
          ...(config.items ? {
            items: config.items.map((item: any) => ({
              ...item,
              scoring: { points: Number(item.scoring?.points) || 1000 }
            }))
          } : {}),
        }
      };
    });

  const buildPayload = () => {
    const values = getValues();
    const dbShape: any = formValuesToQuiz(values);

    // Drop fields that shouldn't be sent in create/update payloads.
    // `nonce` is injected by quizToFormValues for preview/cookie purposes only.
    const { _id, nonce, ...rest } = dbShape;

    const payload: any = {
      ...rest,
      questions: normalizeQuestions(dbShape.questions),
      questionsSettings: {
        ...(dbShape.questionsSettings || {}),
        limit: dbShape.questionsSettings?.limit ? Number(dbShape.questionsSettings.limit) : 0,
        delay: dbShape.questionsSettings?.delay ? Number(dbShape.questionsSettings.delay) : 2,
      },
      publicLeaderboard: dbShape.publicLeaderboard ? {
        ...dbShape.publicLeaderboard,
        questionsTimeout: dbShape.publicLeaderboard.questionsTimeout ? Number(dbShape.publicLeaderboard.questionsTimeout) : 2,
      } : dbShape.publicLeaderboard,
      callToAction: {
        ...(dbShape.callToAction || {}),
        clickBonusPoints: dbShape.callToAction?.clickBonusPoints ? Number(dbShape.callToAction.clickBonusPoints) : 0,
      },
      scoringResultPage: dbShape.scoringResultPage ? {
        ...dbShape.scoringResultPage,
        bands: dbShape.scoringResultPage.bands?.map((band: any) => ({
          ...band,
          minPercentage: Number(band.minPercentage),
        })),
      } : dbShape.scoringResultPage,
      banners: Array.isArray(dbShape.banners) ? dbShape.banners.map((banner: any) => ({
        ...banner,
        timeout: banner.timeout ? Number(banner.timeout) : undefined,
      })) : dbShape.banners,
    };

    return payload;
  };

  const submitUpdate = (payload: any) => {
    const filtered: any = {};
    Object.keys(payload).forEach((key) => {
      if (UNNECESSARY_KEYS.includes(key)) return;
      filtered[key] = payload[key];
    });
    return dispatch(updateQuiz({ quizId: quiz!._id, data: filtered })).unwrap();
  };

  const submitCreate = (payload: any) => {
    return dispatch(createQuiz(payload)).unwrap();
  };

  const onSubmit = handleSubmit(async () => {
    const payload = buildPayload();

    try {
      if (quiz) {
        await submitUpdate(payload);
      } else {
        await submitCreate(payload);
      }
      onClose();
      onCloseAgentDialog();
    } catch (err) {
      // Errors are surfaced via notifications dispatched by the async thunks.
    }
  });

  // Edit mode: there is nothing extra to collect — auto-save with the current
  // unified form values and close the dialog.
  useEffect(() => {
    if (quiz) onSubmit();
    // eslint-disable-next-line
  }, []);

  if (quiz) return null;
  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle title={t('dialogs.quizAgent.quizDetailsTitle')} onClose={onClose} />
      <DialogContent>
        <div className="min-h-96 overflow-y-auto">
          <div className="mb-4">
            <Controller
              control={control} name="quizName" rules={{ required: isRequired, validate: noWhitespace }}
              render={({ field }) => (
                <Input
                  {...field}
                  label={t('pages.quizForm.quizNameLabel')} id="quizName" name="quizName"
                  required={true}
                  error={Boolean(errors.quizName)}
                  errorText={errors.quizName ? errors.quizName.message : ''}
                  helperText={t('pages.quizForm.quizNameHint')}
                />
              )}
            />
          </div>
          <div className="mb-4">
            <Controller
              control={control} name="namespace" rules={{ required: isRequired }}
              render={({ field }) => (
                <Select
                  {...field}
                  label={t('labels.category')} id="namespace" name="namespace"
                  required={true}
                  options={[
                    { value: '', label: t('labels.chooseCategory') },
                    ...namespaceOptions
                  ]}
                  error={Boolean(errors.namespace)}
                  errorText={errors.namespace ? errors.namespace.message : ''}
                  helperText={t('pages.quizForm.categoryHint')}
                />
              )}
            />
          </div>
          <div className="mb-4">
            <Controller
              control={control} name="startsDate"
              render={({ field }) => (
                <DatePicker
                  {...field}
                  label={t('labels.startDate')}
                  id="startsDate"
                />
              )}
            />
          </div>
          <div className="mb-4">
            <Controller
              control={control} name="endsDate"
              render={({ field }) => (
                <DatePicker
                  {...field}
                  label={t('labels.endDate')}
                  id="endsDate"
                />
              )}
            />
          </div>
          {/* Hidden once already uploaded via the agent's first-step Branding
              section — same `logoImage` field, no need to show it twice in
              one create flow. The Controller (and its required rule) stays
              mounted whenever the field is still empty. */}
          {!logoImageWatcher && (
            <div className="w-full tablet:w-full laptop:w-1/2">
              <div className="mb-4">
                <h2 className="text-white text-2xl leading-6 font-bold">{t('labels.images')}</h2>
              </div>
              <Controller
                control={control} name="logoFormat"
                render={({ field: { onChange, value } }) => (
                  <ButtonGroup size="small" style={{ marginBottom: '16px' }}>
                    <StyledMuiButton
                      variant={value === MediaFormats.Logo ? 'contained' : 'outlined'}
                      onClick={() => onChange(MediaFormats.Logo)}
                    >{t('labels.squareLogo')}</StyledMuiButton>
                    <StyledMuiButton
                      variant={value === MediaFormats.RectLogo ? 'contained' : 'outlined'}
                      onClick={() => onChange(MediaFormats.RectLogo)}
                    >{t('labels.rectangularLogo')}</StyledMuiButton>
                  </ButtonGroup>
                )}
              />
              <Controller
                control={control} name="logoImage" rules={{ required: isRequired }}
                render={({ field }) => (
                  <ImageUpload
                    {...field}
                    key={logoFormatWatcher}
                    className="w-1/4"
                    format={logoFormatWatcher}
                    required={true}
                    label="labels.logoImage"
                    error={Boolean(errors.logoImage)}
                    errorText={errors.logoImage ? errors.logoImage.message : ''}
                  />
                )}
              />
            </div>
          )}
          {[QuizTypes.Scoring, QuizTypes.Prediction, QuizTypes.Poll, QuizTypes.RankingPoll, QuizTypes.TeamSelector, QuizTypes.BeatTheExpert, QuizTypes.Streak].includes(typeWatcher) && (
            <div className="flex items-center mb-4">
              <span className="block text-white text-base font-semibold mr-3">{t('dialogs.quizAgent.enableLiveLeaderboard')}</span>
              <Controller
                control={control} name="publicLeaderboard.enabled"
                render={({ field: { onChange, value } }) => (
                  <Toggle
                    id="publicLeaderboardEnabled"
                    checked={value}
                    onChange={onChange}
                  />
                )}
              />
            </div>
          )}
        </div>
      </DialogContent>
      <DialogActions>
        <Button variant="contained" onClick={onSubmit} loading={loading}>
          {quiz ? t('buttons.save') : t('buttons.create')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default QuizAgentFinalizeDialog;
